import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { VStack, Text } from "@gluestack-ui/themed";
import { ActionSheet } from "@repo/core/components/layout/Actionsheet";
import { IActionsheetRefProps } from "@repo/core/components/layout/Actionsheet/types";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import FixedLoading from "@repo/core/components/core/FixedLoading";
import Spinner from "@repo/core/components/core/Spinner";

import {
  getProposalOptionsBySituationCode,
  normalizeProposalSituationCode
} from "../../utils";
import { ProposalOptionsItem } from "./ProposalOptionsItem";
import { IProposalOptionsSheetRef } from "./types";
import { ProposalOptionsItemProps } from "./ProposalOptionsItem/types";
import { PROPOSAL_ACTIONS } from "@repo/core/constants/proposals";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { useFidelityProposal } from "@repo/core/screens/ProposalEffectiveFlows/Fidelity/hooks/useFidelityProposal";
import { usePreProposal } from "@repo/core/screens/PreProposal/hooks/usePreProposal";
import { getProposalEffectuationErrorMessage } from "@repo/core/screens/Proposals/rules/proposalEffectuationError";
import { getProposalEffectuationFlow } from "@repo/core/screens/Proposals/rules/proposalEffectuationFlow";
import { getCreditTableEntryNavigation } from "@repo/core/screens/Proposals/rules/creditTableCompletionFlow";
import { effectuateRejectedFidelityProposal } from "@repo/core/screens/Proposals/services/effectuateRejectedFidelityProposal.service";
import {
  PROPOSTA_SITUACAO,
  ProposalTypeEnum,
  ProposalTypeValue
} from "@repo/core/services/entities/Proposals";
import { MobileProposalResource } from "@repo/core/services/resources/mobile-proposal";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { apiInstance } from "@repo/core/services/instances/api";
import useToast from "@repo/core/components/core/Toast";
import { useUserPermissions } from "@repo/core/hooks/useUserPermissions";

const proposalsResource = new ProposalsResource(apiInstance);
const mobileProposalResource = new MobileProposalResource(apiInstance);
const ACTION_SHEET_CLOSE_DELAY_MS = 350;

const waitForNextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

const waitForActionSheetClose = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ACTION_SHEET_CLOSE_DELAY_MS);
  });

export const ProposalOptionsSheet = forwardRef<
  IProposalOptionsSheetRef,
  Record<never, never>
>(function ProposalOptionsSheet(_, ref) {
  const { push, replace } = useNavigation();
  const toast = useToast();
  const { permissions } = useUserPermissions();

  const { clearCreditProposal, setPostSaveAction } = useCreditProposal();
  const { clearFidelityProposal } = useFidelityProposal();
  const { startQueueProposalEdit } = usePreProposal();

  const [registerId, setRegisterId] = useState<string | null>(null);
  const [proposalStatus, setProposalStatus] = useState<string | null>(null);
  const [proposalType, setProposalType] = useState<ProposalTypeValue>();
  const [proposalOptions, setProposalOptions] = useState<
    Omit<ProposalOptionsItemProps, "onPress">[] | null
  >(null);
  const [loadingAction, setLoadingAction] = useState<PROPOSAL_ACTIONS | null>(
    null
  );
  const actionInFlightRef = useRef(false);
  const actionSheetRef = useRef<IActionsheetRefProps>(null);

  useImperativeHandle(
    ref,
    (): IProposalOptionsSheetRef => ({
      open: (id: string, statusCode: string, proposalTypeCode?: string) => {
        const proposalTypeOptions = getProposalOptionsBySituationCode(
          statusCode,
          proposalTypeCode,
          undefined,
          permissions
        );
        setRegisterId(id);
        setProposalStatus(normalizeProposalSituationCode(statusCode));
        setProposalType(proposalTypeCode);
        setProposalOptions(proposalTypeOptions);
        // Open modal immediately - no external state updates blocking
        actionSheetRef.current?.open();
      },
      close: () => {
        actionSheetRef.current?.close();
      }
    }),
    [permissions]
  );

  const navigateToEffectuationSuccess = useCallback(
    (isCreditProposal: boolean) => {
      if (!registerId) {
        return;
      }

      replace(ROUTES.proposalEffectiveSuccess, {
        proposalId: registerId,
        isCreditProposal
      });
    },
    [registerId, replace]
  );

  const closeActionSheet = useCallback(async () => {
    actionSheetRef.current?.close();
    await waitForActionSheetClose();
  }, []);

  const effectuateProposal = useCallback(
    async (action: PROPOSAL_ACTIONS): Promise<boolean> => {
      if (!registerId) {
        return false;
      }

      await waitForNextFrame();

      let errorMessage: string | null = null;
      let shouldNavigateToSuccess = false;

      try {
        const response =
          proposalStatus === PROPOSTA_SITUACAO.APPROVED_AUTOMATICALLY &&
          proposalType === ProposalTypeEnum.FIDELITY
            ? await effectuateRejectedFidelityProposal({
                lngProposta: registerId,
                mobileProposalResource,
                proposalsResource
              })
            : await proposalsResource.effectuateProposal(registerId);

        if (response.status === 200) {
          shouldNavigateToSuccess = true;
        } else {
          errorMessage = getProposalEffectuationErrorMessage(response);
        }
      } catch (error: unknown) {
        errorMessage = getProposalEffectuationErrorMessage(error);
      } finally {
        actionSheetRef.current?.close();
      }

      if (shouldNavigateToSuccess) {
        navigateToEffectuationSuccess(
          action === PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL
        );
        return true;
      }

      if (errorMessage) {
        toast.error("Erro ao efetivar proposta", errorMessage);
      }

      return false;
    },
    [
      navigateToEffectuationSuccess,
      proposalStatus,
      proposalType,
      registerId,
      toast
    ]
  );

  const handleEffectiveAction = useCallback(
    async (action: PROPOSAL_ACTIONS): Promise<boolean> => {
      if (!registerId) {
        await closeActionSheet();
        return false;
      }

      switch (getProposalEffectuationFlow(proposalStatus || "", proposalType)) {
        case "creditForm":
          await closeActionSheet();
          clearCreditProposal();
          push(ROUTES.creditProposalPersonalData, {
            proposalId: registerId
          });
          return true;

        case "fidelityForm":
          await closeActionSheet();
          clearFidelityProposal();
          push(ROUTES.fidelityProposalCompleteRegistration, {
            proposalId: registerId
          });
          return true;

        case "effectuate":
          await closeActionSheet();
          return effectuateProposal(action);

        default:
          console.warn(
            `Proposal status ${proposalStatus} is not mapped for effective proposal action`
          );
          return false;
      }
    },
    [
      clearCreditProposal,
      clearFidelityProposal,
      closeActionSheet,
      effectuateProposal,
      proposalStatus,
      proposalType,
      push,
      registerId
    ]
  );

  const handleOnPressOption = useCallback(
    async (action: PROPOSAL_ACTIONS) => {
      if (actionInFlightRef.current) {
        return;
      }

      actionInFlightRef.current = true;
      setLoadingAction(action);
      let keepsLockDuringNavigation = false;

      try {
        switch (action) {
          case PROPOSAL_ACTIONS.SEE_PROPOSAL:
            await closeActionSheet();
            if (registerId) {
              push(ROUTES.proposalsViewInfo, {
                proposalId: registerId
              });
              keepsLockDuringNavigation = true;
            }
            break;
          case PROPOSAL_ACTIONS.EDIT_PROPOSAL:
            await closeActionSheet();
            if (registerId) {
              await startQueueProposalEdit(registerId, "proposal");
              keepsLockDuringNavigation = true;
            }
            break;
          case PROPOSAL_ACTIONS.EDIT_PRE_PROPOSAL:
            await closeActionSheet();
            if (registerId) {
              await startQueueProposalEdit(registerId, "preProposal");
              keepsLockDuringNavigation = true;
            }
            break;
          case PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL:
          case PROPOSAL_ACTIONS.FIDELITY_EFFECTIVE:
            keepsLockDuringNavigation = await handleEffectiveAction(action);
            break;
          case PROPOSAL_ACTIONS.SEND_TO_CREDIT_TABLE:
            await closeActionSheet();
            if (registerId) {
              const entryNavigation = getCreditTableEntryNavigation(
                proposalStatus,
                registerId
              );

              if (entryNavigation.postSaveAction) {
                clearCreditProposal();
                setPostSaveAction(entryNavigation.postSaveAction);
              }

              push(entryNavigation.route, entryNavigation.params);
              keepsLockDuringNavigation = true;
            }
            break;
          default:
            await closeActionSheet();
            console.log("action not found");
            break;
        }
      } finally {
        if (keepsLockDuringNavigation) {
          await waitForActionSheetClose();
        }
        actionInFlightRef.current = false;
        setLoadingAction(null);
      }
    },
    [
      registerId,
      proposalStatus,
      push,
      closeActionSheet,
      clearCreditProposal,
      setPostSaveAction,
      handleEffectiveAction,
      startQueueProposalEdit
    ]
  );

  const isProcessingEffectuation =
    loadingAction === PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL ||
    loadingAction === PROPOSAL_ACTIONS.FIDELITY_EFFECTIVE;

  return (
    <>
      {loadingAction ? <FixedLoading /> : null}
      <ActionSheet
        ref={actionSheetRef}
        backdropCanClose={!isProcessingEffectuation}
      >
        {isProcessingEffectuation ? (
          <VStack w="$full" p="$6" gap="$4" alignItems="center">
            <Spinner />
            <VStack gap="$1" alignItems="center">
              <Text fontSize="$md" fontWeight={600} color="$textLight900">
                Efetivando proposta
              </Text>
              <Text fontSize="$sm" color="$textLight600" textAlign="center">
                Aguarde enquanto processamos a efetivação.
              </Text>
            </VStack>
          </VStack>
        ) : (
          <VStack w="$full" p="$2" mb="$2">
            <VStack mb="$4" px="$3">
              <Text fontSize="$md" fontWeight={500} color="$textLight600">
                Opções
              </Text>
            </VStack>

            <VStack>
              {proposalOptions?.map((option) => (
                <ProposalOptionsItem
                  key={option.label}
                  {...option}
                  isDisabled={!!loadingAction}
                  isLoading={loadingAction === option.action}
                  onPress={handleOnPressOption}
                />
              ))}
            </VStack>
          </VStack>
        )}
      </ActionSheet>
    </>
  );
});
